import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { rethrowUnlessAbort } from "../utils/fetch-error.util"
import { formatMoney } from "../utils/server-format.util"
import { tableClientToken } from "../utils/table_client_token"
import TableFrameNavigator from "../utils/table_frame_navigator"
import TableQueryState from "../utils/table_query_state"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  breakdownNetLabel: "",
  breakdownVatLabel: "",
  breakdownTotalLabel: "",
  unconvertibleNoticeTemplate: ""
}

// Connects to data-controller="receivables-overview"
export default class extends Controller {
  #initialSyncFrame = null
  #syncRequestAbortController = null
  #syncRequestGeneration = 0

  static targets = [
    "track",
    "tile",
    "chart",
    "prevButton",
    "nextButton",
    "metricValue",
    "metricSubtext",
    "trendBadge",
    "trendValue",
    "trendIcon",
    "syncSignal",
    "unconvertibleNotice",
    "unconvertibleNoticeText"
  ]
  static values = {
    translations: { type: Object, default: {} },
    activeMetric: String,
    userId: Number,
    numberFormat: { type: Object, default: {} },
    reportCurrencyCode: String,
    syncUrl: String
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.chartInstances = []
    this.chartInstancesByMetric = new Map()
    this.valueAnimationState = new WeakMap()
    this.element.dataset.chartsReady = "false"
    this.pendingRealtimeRefreshTimeout = null
    this.clientToken = tableClientToken()
    this.tableNavigator = new TableFrameNavigator({ frameId: "documents", tableKey: "documents:invoice" })
    this.renderCharts()
    this.#runInitialValueAnimation()
    this.#initializeCurrencyBreakdownPopovers()
    this.updateCarouselState()
    this.scrollActiveTileIntoView()
    this._resizeTimeout = null
    this.handleResize = () => {
      clearTimeout(this._resizeTimeout)
      this._resizeTimeout = setTimeout(() => this.updateCarouselState(), 150)
    }
    window.addEventListener("resize", this.handleResize)

    // The server renders the overview for the current URL params, so the
    // initial sync — a second full request against the same params — is
    // skipped when it says so. The flag is cleared right away: a snapshot
    // Turbo restores later (back/forward) is stale by then and syncs as before.
    const renderedByServer = this.element.dataset.receivablesOverviewRendered === "true"
    delete this.element.dataset.receivablesOverviewRendered

    if (this.hasTileTarget && !renderedByServer) {
      this.#initialSyncFrame = requestAnimationFrame(() => {
        this.#initialSyncFrame = null
        if (!this.element.isConnected) return

        this.syncOverview(new URLSearchParams(window.location.search))
      })
    }
  }

  disconnect() {
    clearTimeout(this._resizeTimeout)
    this.element.dataset.chartsReady = "false"
    if (this.#initialSyncFrame !== null) cancelAnimationFrame(this.#initialSyncFrame)
    this.#initialSyncFrame = null
    this.#syncRequestGeneration += 1
    this.#syncRequestAbortController?.abort()
    this.#syncRequestAbortController = null
    this.#clearPendingRealtimeRefresh()
    this.tableNavigator?.disconnect()
    this.#cancelValueAnimations()
    this.destroyCharts()
    window.removeEventListener("resize", this.handleResize)
  }

  applyMetric(event) {
    const metric = event.currentTarget.dataset.metricKey
    if (!metric) return

    const params = new URLSearchParams(window.location.search)
    const isTogglingOff = this.activeMetricValue === metric

    if (isTogglingOff) {
      this.#clearMetricFilters(params)
      this.activeMetricValue = null
      this.#setActiveMetric(null)
    } else {
      this.#setMetricFilters(params, metric)
      this.activeMetricValue = metric
      this.#setActiveMetric(metric)
    }

    params.delete("quickFilter")
    event.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" })
    this.#updateDocumentsFrame(params, { preserveScroll: { x: true, y: false } })
    this.syncOverview(params)
  }

  scrollLeft() {
    if (!this.hasTrackTarget) return
    this.trackTarget.scrollBy({ left: -this.#scrollStep(), behavior: "smooth" })
  }

  scrollRight() {
    if (!this.hasTrackTarget) return
    this.trackTarget.scrollBy({ left: this.#scrollStep(), behavior: "smooth" })
  }

  updateCarouselState() {
    if (!this.hasTrackTarget) return
    const { scrollLeft, scrollWidth, clientWidth } = this.trackTarget
    const atStart = scrollLeft <= 0
    const atEnd = Math.ceil(scrollLeft + clientWidth) >= scrollWidth
    const overflows = scrollWidth > clientWidth

    if (this.hasPrevButtonTarget) this.prevButtonTarget.disabled = atStart
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = atEnd

    const wrapper = this.trackTarget.parentElement
    wrapper.classList.toggle("has-overflow-left", overflows && !atStart)
    wrapper.classList.toggle("has-overflow-right", overflows && !atEnd)
  }

  renderCharts() {
    this.destroyCharts()
    this.element.dataset.chartsReady = "false"

    this.chartTargets.forEach((canvas) => {
      const series = this.#parseSeries(canvas.dataset.series)
      const data = series.length > 0 ? series : [0, 0, 0, 0, 0, 0]
      const minValue = Math.min(...data)
      const maxValue = Math.max(...data)
      const hasValueRange = maxValue > minValue
      const yPadding = hasValueRange ? (maxValue - minValue) * 0.01 : 0
      const yScaleOptions = hasValueRange
        ? { display: false, min: minValue - yPadding, max: maxValue + yPadding }
        : { display: false }
      const context = canvas.getContext("2d")
      if (!context) return
      const gradient = context.createLinearGradient(0, 0, 0, canvas.height || 48)
      gradient.addColorStop(0, "rgba(105, 65, 198, 0.12)")
      gradient.addColorStop(0.62, "rgba(105, 65, 198, 0.045)")
      gradient.addColorStop(1, "rgba(105, 65, 198, 0)")

      const chart = new Chart(context, {
        type: "line",
        data: {
          labels: data.map((_, index) => index + 1),
          datasets: [
            {
              data,
              borderColor: "#6941C6",
              borderWidth: 1.5,
              backgroundColor: gradient,
              fill: true,
              tension: 0.18,
              cubicInterpolationMode: "monotone",
              pointRadius: 0
            }
          ]
        },
        options: {
          animation: { duration: 0 },
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
          layout: { padding: { top: 1, bottom: 1, left: 0, right: 0 } },
          maintainAspectRatio: false,
          responsive: true,
          scales: {
            x: { display: false },
            y: yScaleOptions
          },
          elements: {
            line: { capBezierPoints: false },
            point: { radius: 0, hoverRadius: 0, hitRadius: 0 }
          }
        }
      })

      this.chartInstances.push(chart)
      if (canvas.dataset.metricKey) {
        this.chartInstancesByMetric.set(canvas.dataset.metricKey, chart)
      }
    })

    requestAnimationFrame(() => {
      this.element.dataset.chartsReady = "true"
    })
  }

  destroyCharts() {
    if (!this.chartInstances) return
    this.chartInstances.forEach((chart) => chart.destroy())
    this.chartInstances = []
    this.chartInstancesByMetric = new Map()
  }

  refreshOverview(overview) {
    if (!overview || typeof overview !== "object") return
    this.#applyOverview(overview)
  }

  scrollActiveTileIntoView() {
    if (!this.hasTrackTarget || !this.hasTileTarget || !this.activeMetricValue) return
    const tile = this.tileTargets.find((node) => node.dataset.metricKey === this.activeMetricValue)
    if (!tile) return
    tile.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" })
  }

  #scrollStep() {
    return Math.max(240, this.trackTarget.clientWidth * 0.75)
  }

  #setMetricFilters(params, metric) {
    const today = new Date()
    const asDate = (date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    const addDays = (date, days) => {
      const next = new Date(date)
      next.setDate(next.getDate() + days)
      return next
    }
    const openLifecycleStatuses = ["issued", "sent"]
    const openPaymentStatuses = ["unpaid", "partially_paid"]
    const paidPaymentStatuses = ["paid", "overpaid"]

    params.delete("status[]")
    params.delete("paymentStatus[]")
    params.delete("dueDateFrom")
    params.delete("dueDateTo")
    params.delete("paidFrom")
    params.delete("paidTo")
    params.delete("receivablesCollectedLast30")
    params.delete("hasAmountDue")

    if (metric === "collected_last_30_days") {
      paidPaymentStatuses.forEach((status) => params.append("paymentStatus[]", status))
      params.set("receivablesCollectedLast30", "1")
      return
    }

    openLifecycleStatuses.forEach((status) => params.append("status[]", status))
    openPaymentStatuses.forEach((status) => params.append("paymentStatus[]", status))
    // hasAmountDue=1 mirrors ReceivablesOverviewQuery's `amount_due > 0`
    // predicate — without it the document table includes invoices in
    // open status with zero balance (settled via tax-doc deductions),
    // not matching the count shown in the receivables-overview card above.
    // Exception: due_next_30_days_count in the query intentionally omits
    // that predicate (see ReceivablesOverviewQuery#due_next_30_days_count),
    // so the click-through filter must omit it too to keep counts aligned.
    if (metric !== "due_next_30_days") {
      params.set("hasAmountDue", "1")
    }
    if (metric === "due_next_30_days") {
      params.set("dueDateFrom", asDate(today))
      params.set("dueDateTo", asDate(addDays(today, 30)))
    } else if (metric === "total_overdue") {
      params.set("dueDateTo", asDate(addDays(today, -1)))
    }
  }

  #clearMetricFilters(params) {
    params.delete("status[]")
    params.delete("paymentStatus[]")
    params.delete("dueDateFrom")
    params.delete("dueDateTo")
    params.delete("paidFrom")
    params.delete("paidTo")
    params.delete("receivablesCollectedLast30")
    params.delete("hasAmountDue")
  }

  #updateDocumentsFrame(params, options = {}) {
    const { pushHistory = true, preserveTableScroll = false, preserveScroll = null } = options
    this.tableNavigator.navigate(new TableQueryState(params), {
      history: pushHistory ? "push" : "none",
      reason: pushHistory ? "metric" : "realtime",
      preserveTableScroll,
      preserveScroll
    })
  }

  #refreshFromCurrentQuery() {
    const params = new URLSearchParams(window.location.search)
    this.syncOverview(params)
    this.#updateDocumentsFrame(params, { pushHistory: false, preserveTableScroll: true })
  }

  #scheduleRealtimeRefresh() {
    this.#clearPendingRealtimeRefresh()
    this.pendingRealtimeRefreshTimeout = window.setTimeout(() => {
      this.#refreshFromCurrentQuery()
      this.pendingRealtimeRefreshTimeout = null
    }, 300)
  }

  #clearPendingRealtimeRefresh() {
    if (!this.pendingRealtimeRefreshTimeout) return
    window.clearTimeout(this.pendingRealtimeRefreshTimeout)
    this.pendingRealtimeRefreshTimeout = null
  }

  syncSignalTargetConnected() {
    if (!this.syncSignalInitialized) {
      this.syncSignalInitialized = true
      return
    }
    if (
      this.hasSyncSignalTarget &&
      this.syncSignalTarget.dataset.originClientToken &&
      this.syncSignalTarget.dataset.originUserId === this.userIdValue.toString() &&
      this.syncSignalTarget.dataset.originClientToken === this.clientToken
    ) {
      this.syncOverview(new URLSearchParams(window.location.search))
      return
    }
    this.#scheduleRealtimeRefresh()
  }

  #setActiveMetric(metric) {
    this.tileTargets.forEach((tile) => {
      const isActive = tile.dataset.metricKey === metric
      tile.classList.toggle("is-active", isActive)
      tile.setAttribute("aria-pressed", isActive ? "true" : "false")
    })
  }

  async syncOverview(params) {
    this.#syncRequestAbortController?.abort()
    const abortController = new AbortController()
    this.#syncRequestAbortController = abortController
    const generation = ++this.#syncRequestGeneration
    const syncParams = new URLSearchParams(params)
    syncParams.set("receivablesOverview", "1")
    const url = new URL(this.syncUrlValue, window.location.origin)
    url.search = syncParams.toString()

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        signal: abortController.signal
      })
      // fetch resolves for 5xx too; returning here would have made a broken
      // endpoint indistinguishable from a successful no-op.
      if (!response.ok) throw new Error(`Receivables overview sync failed: HTTP ${response.status}`)

      const payload = await response.json()
      if (generation !== this.#syncRequestGeneration || !this.element.isConnected) return
      if (!payload?.overview) return
      this.activeMetricValue = payload.active_metric || null
      this.#setActiveMetric(this.activeMetricValue)
      this.#applyOverview(payload.overview)
    } catch (error) {
      // Keep the current UI state either way — a half-synced overview is worse
      // than a stale one — but let a real failure reach monitoring.
      rethrowUnlessAbort(error)
    } finally {
      if (this.#syncRequestAbortController === abortController) {
        this.#syncRequestAbortController = null
      }
    }
  }

  #applyOverview(overview) {
    const valueElementsByKey = this.#elementsByMetricKey(this.metricValueTargets)
    const subtextElementsByKey = this.#elementsByMetricKey(this.metricSubtextTargets)
    const trendElementsByKey = this.#elementsByMetricKey(this.trendBadgeTargets)
    const trendValueElementsByKey = this.#elementsByMetricKey(
      this.trendValueTargets,
      (el) => el.closest("[data-metric-key]")?.dataset.metricKey
    )
    const trendIconElementsByKey = this.#elementsByMetricKey(
      this.trendIconTargets,
      (el) => el.closest("[data-metric-key]")?.dataset.metricKey
    )

    Object.entries(overview).forEach(([metricKey, metricData]) => {
      // `unconvertible_currencies` is a top-level array, not a metric tile —
      // handle it separately so it isn't treated as a metric.
      if (metricKey === "unconvertible_currencies") return

      const valueEl = valueElementsByKey.get(metricKey)
      if (valueEl) {
        const nextValue = Number(metricData.value || 0)
        const currentValue = Number(valueEl.dataset.value || 0)
        if (Math.abs(nextValue - currentValue) > 0.001) {
          this.#animateValueChange(valueEl, currentValue, nextValue)
        }
        const popoverContent = this.#currencyBreakdownPopoverContent(metricData.currency_breakdown, nextValue)
        valueEl.dataset.popoverContentValue = popoverContent || ""
        valueEl.dataset.currencyBreakdown = JSON.stringify(metricData.currency_breakdown || [])
      }

      const subtextEl = subtextElementsByKey.get(metricKey)
      if (subtextEl && subtextEl.textContent?.trim() !== metricData.subtext) {
        subtextEl.textContent = metricData.subtext
      }

      const trendEl = trendElementsByKey.get(metricKey)
      const trendValueEl = trendValueElementsByKey.get(metricKey)
      const trendIconEl = trendIconElementsByKey.get(metricKey)
      this.updateTrendBadge(trendEl, trendValueEl, trendIconEl, metricData?.trend)

      const nextSeries =
        Array.isArray(metricData.series) && metricData.series.length > 0 ? metricData.series : [0, 0, 0, 0, 0, 0]
      this.#updateChart(metricKey, nextSeries)
    })

    this.#updateUnconvertibleNotice(overview.unconvertible_currencies)
  }

  #updateUnconvertibleNotice(currencies) {
    if (!this.hasUnconvertibleNoticeTarget) return

    const list = Array.isArray(currencies) ? currencies.filter(Boolean) : []
    const show = list.length > 0

    this.unconvertibleNoticeTarget.hidden = !show
    if (!show) return

    const text = this.#interpolate(this.#texts.unconvertibleNoticeTemplate, { currencies: list.join(", ") })
    if (this.hasUnconvertibleNoticeTextTarget) {
      this.unconvertibleNoticeTextTarget.textContent = text
    } else {
      this.unconvertibleNoticeTarget.textContent = text
    }
  }

  #interpolate(template, values) {
    return String(template || "").replace(/%\{([^}]+)\}/g, (_match, key) => values[key] ?? "")
  }

  #updateChart(metricKey, nextSeries) {
    const chart = this.chartInstancesByMetric.get(metricKey)
    if (!chart) return

    const previous = chart.data?.datasets?.[0]?.data || []
    if (
      previous.length === nextSeries.length &&
      previous.every((value, index) => Number(value) === Number(nextSeries[index]))
    ) {
      return
    }

    chart.data.datasets[0].data = nextSeries
    chart.data.labels = nextSeries.map((_, index) => index + 1)

    const minValue = Math.min(...nextSeries)
    const maxValue = Math.max(...nextSeries)
    const hasValueRange = maxValue > minValue
    const yPadding = hasValueRange ? (maxValue - minValue) * 0.01 : 0
    if (hasValueRange) {
      chart.options.scales.y.min = minValue - yPadding
      chart.options.scales.y.max = maxValue + yPadding
    } else {
      delete chart.options.scales.y.min
      delete chart.options.scales.y.max
    }

    chart.update("none")
  }

  // number_to_currency(value, currency: reporting currency) — the server's
  // string for the tile it rendered (number_format_data).
  #formatAmount(value) {
    return formatMoney(value, this.reportCurrencyCodeValue, this.numberFormatValue)
  }

  #formatAmountWithCode(value, currencyCode) {
    return formatMoney(value, currencyCode, this.numberFormatValue)
  }

  #escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  #currencyBreakdownPopoverContent(breakdown, totalValue) {
    if (!Array.isArray(breakdown) || breakdown.length === 0) return ""

    const netLabel = this.#escapeHtml(this.#texts.breakdownNetLabel || "Excl. VAT")
    const vatLabel = this.#escapeHtml(this.#texts.breakdownVatLabel || "VAT")
    const totalLabelInline = this.#escapeHtml(this.#texts.breakdownTotalLabel || "Total")

    const currencySections = breakdown
      .map((row) => {
        const code = String(row.currency || "").toUpperCase()
        const netValue = this.#escapeHtml(
          this.#formatAmountWithCode(row.net_original_amount ?? row.original_amount, code)
        )
        const vatValue = this.#escapeHtml(this.#formatAmountWithCode(row.vat_original_amount ?? 0, code))
        const grossValue = this.#escapeHtml(
          this.#formatAmountWithCode(row.gross_original_amount ?? row.original_amount, code)
        )

        return `
        <div class="receivables-overview__breakdown-currency">
          <div class="receivables-overview__breakdown-currency-code">${this.#escapeHtml(code)}</div>
          <div class="receivables-overview__breakdown-row">
            <span class="receivables-overview__breakdown-label">${netLabel}</span>
            <span class="receivables-overview__breakdown-value">${netValue}</span>
          </div>
          <div class="receivables-overview__breakdown-row">
            <span class="receivables-overview__breakdown-label">${vatLabel}</span>
            <span class="receivables-overview__breakdown-value">${vatValue}</span>
          </div>
          <div class="receivables-overview__breakdown-row receivables-overview__breakdown-row--subtotal">
            <span class="receivables-overview__breakdown-label">${totalLabelInline}</span>
            <span class="receivables-overview__breakdown-value">${grossValue}</span>
          </div>
        </div>
      `
      })
      .join("")

    const totalBaseLabel = this.#texts.breakdownTotalLabel || "Total"
    const totalLabel = this.#escapeHtml(
      this.reportCurrencyCodeValue
        ? `${totalBaseLabel} (${this.reportCurrencyCodeValue.toUpperCase()})`
        : totalBaseLabel
    )
    const totalAmount = this.#escapeHtml(this.#formatAmount(totalValue))

    return `
      <div class="receivables-overview__breakdown">
        ${currencySections}
        <div class="receivables-overview__breakdown-row receivables-overview__breakdown-row--total">
          <span class="receivables-overview__breakdown-label">${totalLabel}</span>
          <span class="receivables-overview__breakdown-value">${totalAmount}</span>
        </div>
      </div>
    `
  }

  #initializeCurrencyBreakdownPopovers() {
    this.metricValueTargets.forEach((valueEl) => {
      let breakdown = []
      try {
        const raw = valueEl.dataset.currencyBreakdown || "[]"
        const parsed = JSON.parse(raw)
        breakdown = Array.isArray(parsed) ? parsed : []
      } catch (_error) {
        breakdown = []
      }

      const totalValue = Number(valueEl.dataset.value || 0)
      valueEl.dataset.popoverContentValue = this.#currencyBreakdownPopoverContent(breakdown, totalValue) || ""
    })
  }

  #runInitialValueAnimation() {
    if (!this.hasMetricValueTarget) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return

    this.metricValueTargets.forEach((element, index) => {
      const finalValue = Number(element.dataset.value || 0)
      if (Math.abs(finalValue) <= 0.001) return
      element.textContent = this.#formatAmount(0)
      this.#animateValueChange(element, 0, finalValue, index * 60)
    })
  }

  #animateValueChange(element, fromValue, toValue, delayMs = 0) {
    const from = Number(fromValue || 0)
    const to = Number(toValue || 0)
    if (!element || Math.abs(to - from) <= 0.001) {
      if (element) {
        element.dataset.value = String(to)
        element.textContent = this.#formatAmount(to)
      }
      return
    }

    const previousState = this.valueAnimationState.get(element)
    if (previousState?.rafId) {
      cancelAnimationFrame(previousState.rafId)
    }

    const durationMs = 560
    const startAt = performance.now() + delayMs

    const tick = (now) => {
      if (now < startAt) {
        const waitRaf = requestAnimationFrame(tick)
        this.valueAnimationState.set(element, { rafId: waitRaf })
        return
      }

      const elapsed = Math.min(durationMs, now - startAt)
      const progress = elapsed / durationMs
      const eased = this.#bezierEase(progress, 0.16, 1, 0.3, 1)
      const current = from + (to - from) * eased

      element.textContent = this.#formatAmount(current)

      if (progress < 1) {
        const rafId = requestAnimationFrame(tick)
        this.valueAnimationState.set(element, { rafId })
      } else {
        element.dataset.value = String(to)
        element.textContent = this.#formatAmount(to)
        this.valueAnimationState.delete(element)
      }
    }

    const rafId = requestAnimationFrame(tick)
    this.valueAnimationState.set(element, { rafId })
  }

  #bezierEase(t, x1, y1, x2, y2) {
    if (t <= 0) return 0
    if (t >= 1) return 1

    const cx = 3 * x1
    const bx = 3 * (x2 - x1) - cx
    const ax = 1 - cx - bx

    const cy = 3 * y1
    const by = 3 * (y2 - y1) - cy
    const ay = 1 - cy - by

    const sampleCurveX = (u) => ((ax * u + bx) * u + cx) * u
    const sampleCurveY = (u) => ((ay * u + by) * u + cy) * u
    const sampleDerivativeX = (u) => (3 * ax * u + 2 * bx) * u + cx

    let u = t
    for (let i = 0; i < 6; i += 1) {
      const x = sampleCurveX(u) - t
      const d = sampleDerivativeX(u)
      if (Math.abs(x) < 1e-6 || Math.abs(d) < 1e-6) break
      u -= x / d
    }

    u = Math.min(1, Math.max(0, u))
    return sampleCurveY(u)
  }

  trendStateClass(direction) {
    if (direction === "up") return "is-positive"
    if (direction === "down") return "is-negative"
    return "is-neutral"
  }

  trendIconName(direction) {
    if (direction === "down") return "south_east"
    if (direction === "neutral") return "east"
    return "north_east"
  }

  updateTrendBadge(trendElement, trendValueElement, trendIconElement, trendData) {
    const showTrend = trendData?.reliable === true && trendData?.percentage != null
    const trendDirection = trendData?.direction

    if (trendElement) {
      trendElement.classList.toggle("is-hidden", !showTrend)
      trendElement.classList.remove("is-positive", "is-negative", "is-neutral")
      trendElement.classList.add(this.trendStateClass(trendDirection))
    }

    if (trendValueElement) {
      trendValueElement.textContent = showTrend ? `${trendData.percentage}%` : ""
    }

    if (trendIconElement) {
      trendIconElement.textContent = showTrend ? this.trendIconName(trendDirection) : ""
    }
  }

  #elementsByMetricKey(elements, keyResolver = (element) => element.dataset.metricKey) {
    return elements.reduce((map, element) => {
      const metricKey = keyResolver(element)
      if (metricKey) map.set(metricKey, element)
      return map
    }, new Map())
  }

  #parseSeries(seriesData) {
    if (!seriesData) return []

    try {
      const parsed = JSON.parse(seriesData)
      return Array.isArray(parsed) ? parsed : []
    } catch (_error) {
      return []
    }
  }

  #cancelValueAnimations() {
    if (!this.valueAnimationState) return

    this.metricValueTargets.forEach((element) => {
      const animationState = this.valueAnimationState.get(element)
      if (animationState?.rafId) cancelAnimationFrame(animationState.rafId)
    })
    this.valueAnimationState = new WeakMap()
  }
}
